/**
 * demoScope — DEMO-4. The single answer to "is this row part of the
 * Preview Studio cast, or is it the studio's REAL work?"
 *
 * Why this needs to exist
 * -----------------------
 * Preview Studio is ADDITIVE on several surfaces (GAL-8): the
 * /galleries hub lists the demo cast alongside the studio's own
 * galleries, and `useStudioGallery` falls through to the real fetch
 * for any id it doesn't recognise. That's good for the studio — their
 * work never disappears — but it means a mutation fired while Preview
 * Studio is on could be aimed at EITHER a demo row or a real one.
 *
 * Before DEMO-4 the codebase resolved that ambiguity by refusing every
 * write ("Publishing is not available in this workspace"), which
 * killed the demo. The correct split is by ID:
 *
 *   • demo-scoped id  → the mutation runs against DemoStoreContext.
 *                       Never touches Supabase. Wiped on refresh.
 *   • real id         → the mutation is REFUSED, loudly, with
 *                       `RealDataInPreviewError`. Preview Studio must
 *                       never write to or mutate real studio data,
 *                       and it must never publish real client work.
 *
 * Every demo row in `src/data/demoOverlay.ts` and every row the store
 * creates at runtime carries a `demo-` prefixed id (see
 * `DemoStoreContext.makeAPI.add`, and the `demo-p-…` / `demo-i-…` /
 * `demo-g-…` id generators in the service demo branches). Supabase ids
 * are UUIDs, so the two families cannot collide.
 *
 * Surfaces should ALSO disable the control up front — `isDemoId` is
 * exported so a button can render disabled with an explanatory
 * tooltip instead of letting the studio click and then eat an error.
 * A dead end you discover mid-demo is the worst outcome.
 */

/** Every demo entity id starts with this. Supabase ids are UUIDs. */
export const DEMO_ID_PREFIX = 'demo-';

/** True when `id` belongs to the Preview Studio cast. */
export function isDemoId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(DEMO_ID_PREFIX);
}

/**
 * Thrown when a mutation aimed at REAL studio data fires while
 * Preview Studio is on. Callers should surface `.message` verbatim —
 * it's written to be read by the studio, not by a developer.
 */
export class RealDataInPreviewError extends Error {
  code = 'real_data_in_preview' as const;
  constructor(action: string) {
    super(
      `${action} is turned off while Preview Studio is on, so your real client work can't be changed by accident. Switch Preview Studio off in Settings → Developer and try again.`,
    );
    this.name = 'RealDataInPreviewError';
  }
}

/**
 * Guard for the demo branch of a mutation hook. Call it with the id
 * the mutation is about to change; it throws if that id is real.
 *
 * @param id      the row id the mutation targets
 * @param action  human phrase used in the error, e.g. "Publishing a gallery"
 */
export function assertDemoScoped(
  id: string | null | undefined,
  action: string,
): void {
  if (!isDemoId(id)) throw new RealDataInPreviewError(action);
}
